/**
 * GT Bridge — the ONLY point of contact between cb-ui and GroupTickets state.
 *
 * cb-ui components import selectors, actions and types from HERE.
 * They NEVER import directly from @cb-group-tickets.
 *
 * When GroupTicketsFeatureModule is present:
 *   The 'groupTickets' feature state exists → selectors return real data.
 *   GroupTicketClientBase is provided with the real GroupTicketClient.
 *
 * When GroupTicketsFeatureModule is absent:
 *   createFeatureSelector returns undefined → all selectors return undefined/false.
 *   GroupTicketClientBase is not provided → inject(..., {optional:true}) returns null.
 */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  selectCurrentPool,
  selectCurrentPoolSelections,
} from '@cbcore/NGRX/pool/pool.selectors';

// ─────────── Minimal shared TypeScript types ─────────────────────────────────
export namespace GroupTicketsScope {
  export interface GroupTicketsInfo {
    invitation_count: number;
    group_ticket_pool_ids: number[];
    max_group_tickets?: number;
    [key: string]: any;
  }
  export interface Follower { [key: string]: any; }
  export interface GroupTicketItem { [key: string]: any; }
}

// ─────────── Feature state shape (minimal — just what selectors need) ─────────
interface GtBridgeState {
  info?: GroupTicketsScope.GroupTicketsInfo;
  currentGTSelections?: Record<number, any>;
  selectionsUpdated?: boolean;
  [key: string]: any;
}

// ─────────── Feature selector: safe — returns undefined when GT not loaded ────
const selectGTBridgeState = createFeatureSelector<GtBridgeState>('groupTickets');

// ─────────── Selectors ────────────────────────────────────────────────────────

/** GT info (invitation count etc). undefined when GT module absent. */
export const selectInfo = createSelector(
  selectGTBridgeState,
  (state) => state?.info
);

/** Whether GT selections have been marked as updated. false when GT module absent. */
export const selectSelectionsState = createSelector(
  selectGTBridgeState,
  (state) => state?.selectionsUpdated ?? false
);

/** GT selections for the current pool. undefined when GT module absent or not yet set. */
export const selectCurrentGTSelections = createSelector(
  selectGTBridgeState,
  selectCurrentPool,
  (gtState, pool) =>
    gtState?.currentGTSelections && pool?.id
      ? gtState.currentGTSelections[pool.id]
      : undefined
);

/** Combined pool + GT selections. Consumed by pool-coupon. */
export const selectAllSelections = createSelector(
  selectCurrentPoolSelections,
  selectCurrentGTSelections,
  (poolSelections, gtSelections) => ({ poolSelections, gtSelections })
);

// ─────────── Actions ──────────────────────────────────────────────────────────
// These MUST use the exact same type strings as @cb-group-tickets/+state/group-ticket.actions.ts
// so that GT effects handle actions dispatched from cb-ui components.
//
// Built as plain action-object factories, NOT createAction() — createAction()
// has an import-time side effect that registers its type string in NgRx's
// global strictActionTypeUniqueness registry (enabled workspace-wide, see
// libs/core/cbfe-core.providers.ts/cbfe-core.config.ts). Once cb-group-tickets
// is onboarded, its own group-ticket.actions.ts will call createAction() with
// these exact same type strings — a second createAction() call here would
// register each type twice and crash every consuming app at store bootstrap
// with "Action types are registered more than once" (the same regression
// already hit and fixed for native-deep-link.bridge.ts, see DECISIONS.md).

/** Generate a GT ticket from smartpick selections. */
export const generateGTTicket = (payload: { selections: string }) => ({
  type: '[GT] Generate GT Ticket' as const,
  ...payload,
});

/** Refresh GT info (invitation count, pool ids etc.). */
export const callInfo = (payload: { model: any }) => ({
  type: '[GT] call info' as const,
  ...payload,
});

// ─────────── GroupTicketClientBase (abstract DI service) ──────────────────────
/**
 * Abstract base class for the GroupTicket HTTP client.
 * cb-ui injects this with { optional: true } — gets null when GT module absent.
 * GroupTicketsFeatureModule provides the concrete GroupTicketClient as this token.
 */
export abstract class GroupTicketClientBase {
  abstract getGroupTicket(params: { manager_id: number; pool_id: number }): Observable<any>;
}
